import { useEffect } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useController, UseControllerProps } from 'react-hook-form'
import { toast } from 'react-toastify'

import { TrashIcon } from '@heroicons/react/24/outline'

import useServer, { ImageLoad } from '../../../api/useServerMain'
import SpinnerLoading from '../SpinnerLoading'
import ImageComponent from './Image'

interface MultiDropInterface {
  className?: string
  previewDefault?: ImageLoad[]
  setDisabled?: (_disabled: boolean) => void
}

const MultipleDrop = ({
  className,
  previewDefault,
  ...props
}: MultiDropInterface & UseControllerProps) => {
  const { imgPreview, uploadImg, updateImgLocal, isFetching } = useServer()
  const { field } = useController(props)
  const { getRootProps, open, getInputProps } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpeg', '.jpg'],
    },
    maxSize: 1000000,
    onDropRejected: (e: FileRejection[]) =>
      e[0].errors[0].code === 'file-too-large'
        ? toast.error('La imagen excede los 1MB')
        : toast.error('Error al cargar la imagen'),
    onDropAccepted: async (file: File[]) => {
      const data = new FormData()
      file.map((img) => data.append(`file`, img))
      uploadImg(data, true) //true for multiple param
    },
    noKeyboard: false,
    multiple: true,
  })

  useEffect(() => {
    if (previewDefault) updateImgLocal(previewDefault)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    field.onChange(imgPreview.map((item) => item.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgPreview])

  const deleteImg = (idx: number) => {
    const result = [...imgPreview]
    result.splice(idx, 1)
    updateImgLocal(result)
  }
  return (
    <div className={className}>
      {isFetching ? (
        <div className="flex h-full cursor-pointer items-center justify-center">
          <SpinnerLoading text="Subiendo imágenes, por favor espere ..." />
        </div>
      ) : (
        <div className="flex h-full cursor-pointer items-center justify-center">
          <div
            className={`inline-flex h-full items-center gap-5`}
            {...getRootProps({ onTouchStart: open })}
          >
            <input hidden {...getInputProps()} />
            <svg
              aria-hidden="true"
              className="flex h-20 rounded-md object-center text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 48 48"
            >
              <path
                d={`M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4
                m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28
                m0 0l4 4m4-24h8m-4-4v8m-12 4h.02`}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
            <div className="text-md flex flex-shrink-0 flex-col text-center text-gray-600">
              <div className="relative cursor-pointer rounded py-1 font-medium text-indigo-600 hover:text-indigo-500">
                <p className="text-left">Click para agregar imágenes</p>
              </div>
              <p className="text-left">o arrastre en esta zona</p>
              {/* <p className="text-xs text-gray-500">PNG, JPG, GIF hasta 200kB</p>*/}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-6 gap-3 rounded-lg p-5">
        {imgPreview.map((item, idx) => (
          <div className="relative" key={idx}>
            <ImageComponent
              className="flex h-full w-full items-center rounded-md shadow-md"
              hash={item.hash}
              key={idx}
              src={item.src}
            />
            <TrashIcon
              className={
                `absolute right-2 top-2 h-8 cursor-pointer rounded-md border` +
                ` border-red-500 bg-red-100 p-1 text-red-500 active:shadow-md active:shadow-red-700`
              }
              onClick={() => deleteImg(idx)}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

export default MultipleDrop
